// NEC optional calculation for existing dwelling units.
//
// 2020/2023 packs implement 220.83(A)/(B); a 2026 pack implements the
// restructured single-table 120.83. Where the text is ambiguous the engine
// takes a named, conservative, report-disclosed position (ENGINE_POSITIONS) —
// the same discipline the 220.87 engine uses for its methodology calls.
// Spec: docs/specs/traditional-calcs.md §6.2. Parameters come from the
// edition pack (editions.ts), never inline.

import { Equipment } from '../../../types/project';
import { EditionPack } from '../editions';
import { countsAsCentralResistanceHeat } from '../loadRoles';
import {
  DwellingProfile,
  EvseDemandPolicy,
  TraditionalCalcInput,
  TraditionalCalcResults,
  TraditionalLineItem,
  TraditionalOverrides,
  TraditionalSubmethod,
} from '../types';

export const ENGINE_POSITIONS = {
  // P1 has two halves. Half one (conservative): existing HVAC enters (A)'s
  // pool at nameplate under item (3)(a). Half two (the section grants no
  // discount): (A) contains no larger-of-heating-or-cooling sentence — that
  // rule appears only in (B) — so BOTH heating and cooling count by default.
  // Exactly one of these two strings is pushed, naming which half is active.
  P1_A_BOTH_HVAC_COUNTED:
    'P1 — Existing heating and cooling equipment both enter the 220.83(A) pool at nameplate under item (3)(a). 220.83(A) contains no larger-of-heating-or-cooling allowance (that sentence appears only in 220.83(B)), so no noncoincident exclusion is applied. A 220.60 preparer election is available and is OFF for this calculation.',
  P1_A_NONCOINCIDENT_ELECTION:
    'P1 — Preparer election: the noncoincident-load principle of NEC 220.60 is applied under 220.83(A), counting only the larger of existing heating or cooling. This is a preparer election, not a 220.83 step — 220.83(A) itself grants no such exclusion.',
  P2_EVSE_POOLED_FLOOR_ON:
    'P2 — EVSE is included in the demand-factored pool as equipment located to be on a specific circuit, per the section text (item (3)(a)). The 7,200 VA per-unit minimum applied to it is imported from 220.57 in Part III, which 220.83 does not reference — a conservative election (default on), not a section requirement.',
  P2_EVSE_POOLED_FLOOR_OFF:
    'P2 — EVSE is included in the demand-factored pool as equipment located to be on a specific circuit, per the section text (item (3)(a)). The 220.57-derived 7,200 VA minimum was declined by the preparer; EVSE enters at nameplate as the section calls for.',
  // The 2020 pack has no EVSE load-calculation section, so there is no floor
  // to apply or decline — the pooling reading is still a position and still
  // prints.
  P2_EVSE_POOLED_NO_FLOOR_SECTION:
    'P2 — EVSE is included in the demand-factored pool at nameplate as equipment located to be on a specific circuit, per the section text (item (3)(a)). This edition has no dedicated EVSE load-calculation section, so no per-unit minimum is imported.',
  // The separate-line policy is a preparer election beyond the section text.
  // It used to push nothing at all, so the floor state — which can move the
  // result across the PASS line on a sub-7,200 VA charger — went undisclosed.
  P2_EVSE_SEPARATE_FLOOR_ON:
    'P2 — Preparer election: EVSE is carried on a separate line at a stated percentage instead of in the demand-factored pool. The section text places equipment located to be on a specific circuit (item (3)(a)) in the pool, so the separate line is a conservative preparer election, not a section requirement. The 7,200 VA per-unit minimum imported from 220.57 in Part III, which 220.83 does not reference, is applied to it (default on).',
  P2_EVSE_SEPARATE_FLOOR_OFF:
    'P2 — Preparer election: EVSE is carried on a separate line at a stated percentage instead of in the demand-factored pool. The section text places equipment located to be on a specific circuit (item (3)(a)) in the pool, so the separate line is a conservative preparer election, not a section requirement. The 220.57-derived 7,200 VA minimum was declined by the preparer; EVSE enters at nameplate as the section calls for.',
  P2_EVSE_SEPARATE_NO_FLOOR_SECTION:
    'P2 — Preparer election: EVSE is carried on a separate line at a stated percentage instead of in the demand-factored pool. The section text places equipment located to be on a specific circuit (item (3)(a)) in the pool, so the separate line is a conservative preparer election, not a section requirement. This edition has no dedicated EVSE load-calculation section, so no per-unit minimum is imported; EVSE enters at nameplate.',
  P3_NO_LOAD_MANAGEMENT:
    'P3 — No energy management or power control system reduction is applied.',
  P4_2026_TIER_FILL:
    'P4 — Table 120.83 applies its first 8 kVA at 100% to existing and new load combined. Where existing load is under 8 kVA, TrueLoad fills the remaining tier from new loads lowest-percentage-first (conservative order).',
  P5_NO_CONTINUOUS_EVSE_FACTOR:
    'P5 — No 125% continuous-load factor from Article 625 (625.41/625.42) is applied inside this optional calculation: the section counts EVSE at nameplate. Branch-circuit and feeder sizing for the EVSE itself remains subject to Article 625.',
} as const;

type LoadKind = 'evse' | 'heatPump' | 'stripHeat' | 'ac' | 'general';

interface Row {
  eq: Equipment;
  role: 'existing' | 'proposed';
  kind: LoadKind;
  totalVA: number;
}

function classify(eq: Equipment): LoadKind {
  switch (eq.equipment_type) {
    case 'EVSE':
      return 'evse';
    case 'Heat Pump':
      return 'heatPump';
    case 'Strip Heat':
      return 'stripHeat';
    case 'A/C':
      return 'ac';
    default:
      return 'general';
  }
}

function unitVAOf(eq: Equipment): number {
  if (eq.kw !== null) return eq.kw * 1000;
  if (eq.amps !== null) return eq.amps * eq.voltage;
  return 0;
}

function buildRow(
  eq: Equipment,
  role: 'existing' | 'proposed',
  pack: EditionPack,
  warnings: string[],
  applyEvseFloor: boolean
): Row {
  const kind = classify(eq);
  const quantity = eq.quantity > 0 ? eq.quantity : 1;
  let unitVA = unitVAOf(eq);

  if (kind === 'evse') {
    // The 7,200 VA floor under a 'max7200' edition is imported from 220.57
    // (Part III), which 220.83 does not reference — a defeatable preparer
    // election, disclosed via P2. 120.57's nameplate-else-7200 rule is the
    // 2026 section's own text and is not subject to the election.
    if (pack.evseRule === 'max7200' && applyEvseFloor) {
      unitVA = Math.max(unitVA, 7200);
    } else if (pack.evseRule === 'nameplateElse7200' && unitVA === 0) {
      unitVA = 7200;
      warnings.push(
        `${eq.equipment_name || 'EVSE'}: no rating entered; 7,200 VA applied per ${pack.sectionMap.evse}.`
      );
    }
  } else if (unitVA === 0) {
    warnings.push(
      `${eq.equipment_name || 'Equipment row'}: no kW or amps entered; counted at 0 VA.`
    );
  }
  if (kind === 'evse' && unitVA === 0) {
    warnings.push(
      `${eq.equipment_name || 'EVSE'}: no rating entered; counted at 0 VA.`
    );
  }

  return { eq, role, kind, totalVA: unitVA * quantity };
}

const HEATING_KINDS: LoadKind[] = ['heatPump', 'stripHeat'];
const COOLING_KINDS: LoadKind[] = ['heatPump', 'ac'];

function sideVA(rows: Row[], kinds: LoadKind[]): number {
  return rows
    .filter((r) => kinds.includes(r.kind))
    .reduce((sum, r) => sum + r.totalVA, 0);
}

/** Exactly one P2 variant for every AB-pack calculation that holds an EVSE
 * row: the policy (pooled per the text, or the separate-line election)
 * crossed with the floor state (applied, declined, or no floor in this
 * edition). Every combination prints — a silent one was a real defect: the
 * separate-line policy pushed nothing, so a declined floor on a sub-7,200 VA
 * charger moved the result toward PASS with no disclosure. */
function evsePosition(
  policy: EvseDemandPolicy,
  pack: EditionPack,
  applyEvseFloor: boolean
): string {
  const floor = pack.evseRule === 'max7200' ? (applyEvseFloor ? 'on' : 'off') : 'none';
  if (policy === 'full') {
    if (floor === 'on') return ENGINE_POSITIONS.P2_EVSE_SEPARATE_FLOOR_ON;
    if (floor === 'off') return ENGINE_POSITIONS.P2_EVSE_SEPARATE_FLOOR_OFF;
    return ENGINE_POSITIONS.P2_EVSE_SEPARATE_NO_FLOOR_SECTION;
  }
  if (floor === 'on') return ENGINE_POSITIONS.P2_EVSE_POOLED_FLOOR_ON;
  if (floor === 'off') return ENGINE_POSITIONS.P2_EVSE_POOLED_FLOOR_OFF;
  return ENGINE_POSITIONS.P2_EVSE_POOLED_NO_FLOOR_SECTION;
}

/** True when preparer-stated parameters move the result below what the
 * section's own values would give: a smaller first tier, a tier percentage
 * under 100, a remainder percentage under 40, or a separate-line EVSE
 * percentage under 100. Exported so validation and the Results tab reach the
 * same answer the disclosure states. */
export function statedParametersBelowSection(
  o: TraditionalOverrides,
  pack: EditionPack,
  evsePolicy: EvseDemandPolicy
): boolean {
  return (
    (o.tierKVA != null && o.tierKVA < pack.optional83TierKVA) ||
    (o.tierPercent != null && o.tierPercent < 100) ||
    (o.remainderPercent != null && o.remainderPercent < 40) ||
    (evsePolicy === 'full' && o.evsePercent != null && o.evsePercent < 100)
  );
}

/** A dryer flagged as served by the laundry branch circuit drops out of the
 * pool (item (2) already counts that circuit at 1,500 VA). A 240 V dryer, or
 * one above what a 20 A, 120 V circuit can carry, cannot be on it, so the
 * flag is questioned in the calculation notes rather than honoured silently.
 *
 * The 2026 text generalises this. Verified from p. 70-85 (2026-09-06), 120.83
 * item (3) counts the nameplate of equipment "that are not connected to the
 * branch circuits addressed in 120.83(1) or 120.83(2)" — any equipment on the
 * general-lighting or small-appliance/laundry circuits, not the dryer alone.
 * TrueLoad models only the dryer case, which is the one that occurs in
 * practice; a wider exclusion would need its own flag per row, and until then
 * this engine is conservative under 2026 rather than wrong. */
function laundryDryerNote(row: Row): string | null {
  const qty = row.eq.quantity > 0 ? row.eq.quantity : 1;
  const unit = row.totalVA / qty;
  const volts = row.eq.voltage || 0;
  if (volts < 208 && unit <= 2400) return null;
  return `${row.eq.equipment_name || 'Dryer'}: flagged as served by the laundry branch circuit, but a ${volts} V, ${Math.round(unit)} VA dryer cannot be supplied by the 120 V, 20 A circuit counted at 1,500 VA. It has been left out of the pool as flagged; verify the flag.`;
}

function selectSubmethod(
  pack: EditionPack,
  proposed: Row[],
  override: TraditionalSubmethod | undefined,
  warnings: string[]
): TraditionalSubmethod {
  if (pack.existing83Shape === 'unified2026') {
    if (override && override !== '120.83') {
      warnings.push(
        `Submethod override "${override}" ignored: the ${pack.edition} edition has a single ${pack.sectionMap.existingDwelling} method.`
      );
    }
    return '120.83';
  }
  const hasProposedHVAC = proposed.some((r) =>
    ['heatPump', 'stripHeat', 'ac'].includes(r.kind)
  );
  // (A) is written for the case where no additional air-conditioning or
  // electric space heating is installed. Forcing it onto a project that
  // proposes such equipment would move that equipment from the 100% line into
  // the 40% pool — a discount the section does not grant — so the override is
  // refused and the refusal is disclosed.
  if (override === '220.83A' && hasProposedHVAC) {
    warnings.push(
      `Sub-method override (A) not applied: additional air-conditioning or electric space-heating equipment is proposed, and ${pack.sectionMap.existingDwelling}(A) applies only where none is to be installed. ${pack.sectionMap.existingDwelling}(B) governs this calculation.`
    );
    return '220.83B';
  }
  if (override === '220.83A' || override === '220.83B') return override;
  return hasProposedHVAC ? '220.83B' : '220.83A';
}

function profileLineItems(
  profile: DwellingProfile,
  pack: EditionPack,
  treatment: string,
  warnings: string[]
): { items: TraditionalLineItem[]; totalVA: number } {
  const area = Math.max(0, profile.floorAreaSqFt || 0);
  if (area === 0) {
    warnings.push('Floor area is missing; general lighting counted at 0 VA.');
  }
  const smallAppliance = Math.max(0, profile.smallApplianceCircuits || 0);
  const laundry = Math.max(0, profile.laundryCircuits || 0);

  const addedArea = Math.max(0, profile.addedFloorAreaSqFt || 0);
  const lightingVA = area * pack.lightingVaPerSqFt;
  const addedLightingVA = addedArea * pack.lightingVaPerSqFt;
  const smallApplianceVA = smallAppliance * 1500;
  const laundryVA = laundry * 1500;

  // The worksheet cites what the section itself cites; a null map entry
  // (e.g. the 2020/2026 lighting cross-reference, pending verification)
  // renders the line without a citation.
  const lightingRef = pack.sectionMap.optional83LightingRef;
  const circuitsRef = pack.sectionMap.branchCircuitProvisions;
  const circuitsSuffix = circuitsRef ? ` (NEC ${circuitsRef})` : '';

  const items: TraditionalLineItem[] = [
    {
      id: 'profile-lighting',
      label: `General lighting and receptacles — ${area} ft² × ${pack.lightingVaPerSqFt} VA/ft²${
        lightingRef ? ` (as determined by NEC ${lightingRef})` : ''
      }`,
      origin: 'profile',
      connectedVA: lightingVA,
      treatment,
      demandVA: null,
    },
    ...(addedLightingVA > 0
      ? [
          {
            id: 'profile-added-lighting',
            label: `Added floor area — ${addedArea} ft² × ${pack.lightingVaPerSqFt} VA/ft²${
              lightingRef ? ` (as determined by NEC ${lightingRef})` : ''
            }`,
            origin: 'profile' as const,
            connectedVA: addedLightingVA,
            treatment,
            demandVA: null,
          },
        ]
      : []),
    {
      id: 'profile-small-appliance',
      label: `Small-appliance branch circuits — ${smallAppliance} × 1,500 VA${circuitsSuffix}`,
      origin: 'profile',
      connectedVA: smallApplianceVA,
      treatment,
      demandVA: null,
    },
    {
      id: 'profile-laundry',
      label: `Laundry branch circuits — ${laundry} × 1,500 VA${circuitsSuffix}`,
      origin: 'profile',
      connectedVA: laundryVA,
      treatment,
      demandVA: null,
    },
  ];

  return {
    items,
    totalVA: lightingVA + addedLightingVA + smallApplianceVA + laundryVA,
  };
}

function rowLabel(row: Row): string {
  const name = row.eq.equipment_name || row.eq.equipment_type || 'Equipment';
  const qty = row.eq.quantity > 1 ? ` × ${row.eq.quantity}` : '';
  return `${name}${qty}`;
}

function tierDemand(
  poolVA: number,
  tierKVA: number,
  tierPct: number,
  remainderPct: number
): { tierVA: number; tierDemandVA: number; demandVA: number } {
  const cap = tierKVA * 1000;
  const tierVA = Math.min(poolVA, cap);
  const tierDemandVA = tierPct * tierVA;
  return {
    tierVA,
    tierDemandVA,
    demandVA: tierDemandVA + remainderPct * (poolVA - tierVA),
  };
}

export function calculateExistingDwelling(
  input: TraditionalCalcInput
): TraditionalCalcResults {
  const { profile, serviceCapacityKVA, pack } = input;
  const evsePolicy: EvseDemandPolicy = input.evsePolicy ?? 'pooled';
  const warnings: string[] = [];
  // A provisional pack's provenance travels with every result it produces,
  // reaching the results screen and the report's disclosure block.
  if (pack.provisional) {
    warnings.push(`Edition pack note (NEC ${pack.edition}): ${pack.verification}`);
  }
  const positionsApplied: string[] = [ENGINE_POSITIONS.P3_NO_LOAD_MANAGEMENT];

  const electionUnderA = input.noncoincidentElectionUnderA ?? false;
  const applyEvseFloor = input.applyEvseFloor ?? true;

  const existing = input.existingLoads.map((eq) =>
    buildRow(eq, 'existing', pack, warnings, applyEvseFloor)
  );
  const proposed = input.proposedLoads.map((eq) =>
    buildRow(eq, 'proposed', pack, warnings, applyEvseFloor)
  );
  const allRows = [...existing, ...proposed];

  const submethod = selectSubmethod(pack, proposed, input.submethodOverride, warnings);

  // The larger-of-heating-or-cooling sentence is code text in 220.83(B) and
  // in 120.83. 220.83(A) contains no such sentence — under (A) both heating
  // and cooling enter the pool at nameplate under item (3)(a), and the
  // exclusion exists only as an explicit 220.60 preparer election (default
  // off), named as such on the worksheet.
  const heatingVA = sideVA(allRows, HEATING_KINDS);
  const coolingVA = sideVA(allRows, COOLING_KINDS);
  const heatingWins = heatingVA >= coolingVA;
  const exclusionActive =
    submethod === '120.83' ||
    submethod === '220.83B' ||
    (submethod === '220.83A' && electionUnderA);
  const excludedKind: LoadKind | null =
    !exclusionActive || (heatingVA === 0 && coolingVA === 0)
      ? null
      : heatingWins
        ? 'ac'
        : 'stripHeat';

  const isExcluded = (row: Row): boolean =>
    excludedKind !== null && row.kind === excludedKind;

  const lineItems: TraditionalLineItem[] = [];
  let excludedNoncoincidentVA = 0;

  const governs = heatingWins ? 'heating governs' : 'cooling governs';
  const excludeTreatment =
    submethod === '220.83A'
      ? `excluded — preparer election per NEC ${pack.sectionMap.noncoincident} (${governs})`
      : `excluded — larger of air-conditioning or space heating per the section (${governs})`;

  if (submethod === '120.83') {
    return calculate120x83(input, {
      existing,
      proposed,
      isExcluded,
      excludeTreatment,
      warnings,
      positionsApplied,
    });
  }

  // ----- 220.83(A) / 220.83(B), 2020/2023 packs -----

  const poolRows: Row[] = [];
  const bucketRows: Row[] = [];
  const evseFullRows: Row[] = [];

  const hvacKinds: LoadKind[] = ['heatPump', 'stripHeat', 'ac'];
  const anyHVAC = allRows.some((r) => hvacKinds.includes(r.kind));
  const anyEvse = allRows.some((r) => r.kind === 'evse');

  // 220.83(B) quirk: the table lists only "fewer than four separately
  // controlled space-heating units" at 100%. Standalone strip heat totaling
  // four or more units routes to the pool.
  const heatPumpPresent = allRows.some((r) => r.kind === 'heatPump' && !isExcluded(r));
  const stripUnitCount = allRows
    .filter((r) => r.kind === 'stripHeat' && !isExcluded(r))
    .reduce((sum, r) => sum + (r.eq.quantity > 0 ? r.eq.quantity : 1), 0);
  const stripToPool =
    submethod === '220.83B' && !heatPumpPresent && stripUnitCount >= 4;
  if (stripToPool) {
    warnings.push(
      `Table ${pack.sectionMap.existingDwelling}(B) lists fewer-than-four separately controlled space-heating units at 100%; ${stripUnitCount} units route to the demand-factored pool instead.`
    );
  }

  const laundryDryerRows: Row[] = [];
  for (const row of allRows) {
    if (isExcluded(row)) {
      excludedNoncoincidentVA += row.totalVA;
      continue;
    }
    // Item (3)(c) counts dryers only where they are NOT on the laundry
    // branch circuit already counted at 1,500 VA under item (2).
    if (row.eq.equipment_type === 'Dryer' && row.eq.on_laundry_circuit) {
      const note = laundryDryerNote(row);
      if (note) warnings.push(note);
      laundryDryerRows.push(row);
      continue;
    }
    if (row.kind === 'evse' && evsePolicy === 'full') {
      evseFullRows.push(row);
      continue;
    }
    if (submethod === '220.83B' && hvacKinds.includes(row.kind)) {
      if (row.kind === 'stripHeat' && stripToPool) {
        poolRows.push(row);
      } else {
        bucketRows.push(row);
      }
      continue;
    }
    poolRows.push(row);
  }

  if (submethod === '220.83A' && anyHVAC) {
    positionsApplied.push(
      electionUnderA
        ? ENGINE_POSITIONS.P1_A_NONCOINCIDENT_ELECTION
        : ENGINE_POSITIONS.P1_A_BOTH_HVAC_COUNTED
    );
  }
  if (anyEvse) {
    positionsApplied.push(evsePosition(evsePolicy, pack, applyEvseFloor));
    positionsApplied.push(ENGINE_POSITIONS.P5_NO_CONTINUOUS_EVSE_FACTOR);
  }

  // Preparer-stated demand parameters (AB packs only). Each falls back to the
  // section value; any deviation is disclosed as a stated election below.
  const o = input.overrides ?? {};
  const tierKVAeff = o.tierKVA ?? pack.optional83TierKVA;
  const tierPctEff = (o.tierPercent ?? 100) / 100;
  const remPctEff = (o.remainderPercent ?? 40) / 100;
  const evsePctEff = (o.evsePercent ?? 100) / 100;
  const anyOverride =
    o.tierKVA != null ||
    o.tierPercent != null ||
    o.remainderPercent != null ||
    (o.evsePercent != null && evsePolicy === 'full');
  const belowSection = anyOverride && statedParametersBelowSection(o, pack, evsePolicy);
  if (anyOverride) {
    positionsApplied.push(
      `Preparer-stated demand parameters (election): first ${tierKVAeff} kVA at ${tierPctEff * 100}%, remainder at ${remPctEff * 100}%${
        o.evsePercent != null && evsePolicy === 'full'
          ? `, EVSE on a separate line at ${o.evsePercent}%`
          : ''
      }. The section's values are first ${pack.optional83TierKVA} kVA at 100%, remainder at 40%.${
        belowSection
          ? ' The stated values calculate a LOWER demand than the section values would.'
          : ' The stated values do not calculate below the section values.'
      }`
    );
    if (belowSection) {
      warnings.push(
        "Preparer-stated demand parameters calculate below the section's own values; the authority having jurisdiction's direction for this departure should be on file."
      );
    }
  }

  const poolTreatment = `pool — first ${tierKVAeff} kVA at ${tierPctEff * 100}%, remainder at ${remPctEff * 100}%${anyOverride ? ' (preparer-stated)' : ''}`;
  const profilePart = profileLineItems(profile, pack, poolTreatment, warnings);
  lineItems.push(...profilePart.items);

  let poolVA = profilePart.totalVA;
  for (const row of poolRows) {
    poolVA += row.totalVA;
    lineItems.push({
      id: row.eq.id,
      label: rowLabel(row),
      origin: row.role,
      connectedVA: row.totalVA,
      treatment:
        row.eq.equipment_type === 'Dryer'
          ? `${poolTreatment} — counted under item (3)(c)`
          : poolTreatment,
      demandVA: null,
    });
  }

  for (const row of laundryDryerRows) {
    lineItems.push({
      id: row.eq.id,
      label: rowLabel(row),
      origin: row.role,
      connectedVA: row.totalVA,
      treatment:
        'not separately counted — served by the laundry branch circuit already counted at 1,500 VA (item (2))',
      demandVA: 0,
    });
  }

  let hvacBucketVA = 0;
  for (const row of bucketRows) {
    hvacBucketVA += row.totalVA;
    lineItems.push({
      id: row.eq.id,
      label: rowLabel(row),
      origin: row.role,
      connectedVA: row.totalVA,
      treatment: '100% (air-conditioning / space heating)',
      demandVA: row.totalVA,
    });
  }

  let evseFullVA = 0;
  let evseFullDemandVA = 0;
  for (const row of evseFullRows) {
    evseFullVA += row.totalVA;
    const rowDemand = row.totalVA * evsePctEff;
    evseFullDemandVA += rowDemand;
    lineItems.push({
      id: row.eq.id,
      label: rowLabel(row),
      origin: row.role,
      connectedVA: row.totalVA,
      treatment: `${evsePctEff * 100}% (EVSE on a separate line${
        o.evsePercent != null ? ' — preparer-stated' : ''
      })`,
      demandVA: rowDemand,
    });
  }

  for (const row of allRows.filter(isExcluded)) {
    lineItems.push({
      id: row.eq.id,
      label: rowLabel(row),
      origin: row.role,
      connectedVA: row.totalVA,
      treatment: excludeTreatment,
      demandVA: 0,
    });
  }

  const { tierVA, tierDemandVA, demandVA: poolDemandVA } = tierDemand(
    poolVA,
    tierKVAeff,
    tierPctEff,
    remPctEff
  );
  const totalDemandVA = poolDemandVA + hvacBucketVA + evseFullDemandVA;

  return finalize({
    submethod,
    pack,
    serviceCapacityKVA,
    lineItems,
    totalConnectedVA: poolVA + hvacBucketVA + evseFullVA,
    excludedNoncoincidentVA,
    poolVA,
    tierVA,
    tierDemandVA,
    poolDemandVA,
    hvacBucketVA,
    evseFullVA,
    evseFullDemandVA,
    statedParameters: anyOverride
      ? {
          tierKVA: tierKVAeff,
          tierPercent: tierPctEff * 100,
          remainderPercent: remPctEff * 100,
          evsePercent: evsePctEff * 100,
          belowSection,
        }
      : undefined,
    totalDemandVA,
    positionsApplied,
    warnings,
  });
}

interface Unified2026Context {
  existing: Row[];
  proposed: Row[];
  isExcluded: (row: Row) => boolean;
  excludeTreatment: string;
  warnings: string[];
  positionsApplied: string[];
}

function calculate120x83(
  input: TraditionalCalcInput,
  ctx: Unified2026Context
): TraditionalCalcResults {
  const { profile, serviceCapacityKVA, pack } = input;
  const { existing, proposed, isExcluded, excludeTreatment, warnings, positionsApplied } = ctx;
  const rates = pack.new83Percentages;
  if (!rates) {
    throw new Error(`Edition ${pack.edition} is unified2026 but has no new83Percentages.`);
  }

  const lineItems: TraditionalLineItem[] = [];
  let excludedNoncoincidentVA = 0;

  const existingTreatment = `existing pool — first ${pack.optional83TierKVA} kVA of existing and new at 100%, remainder of existing at 40%`;
  const profilePart = profileLineItems(profile, pack, existingTreatment, warnings);
  lineItems.push(...profilePart.items);

  let existingPoolVA = profilePart.totalVA;
  let newEvseVA = 0;
  let newResistVA = 0;
  let otherNewVA = 0;

  for (const row of [...existing, ...proposed]) {
    if (isExcluded(row)) {
      excludedNoncoincidentVA += row.totalVA;
      lineItems.push({
        id: row.eq.id,
        label: rowLabel(row),
        origin: row.role,
        connectedVA: row.totalVA,
        treatment: excludeTreatment,
        demandVA: 0,
      });
      continue;
    }

    // Item (3)(c): dryers on the already-counted laundry circuit are not
    // separately counted — same rule in the 120.83 list.
    if (row.eq.equipment_type === 'Dryer' && row.eq.on_laundry_circuit) {
      const note = laundryDryerNote(row);
      if (note) warnings.push(note);
      lineItems.push({
        id: row.eq.id,
        label: rowLabel(row),
        origin: row.role,
        connectedVA: row.totalVA,
        treatment:
          'not separately counted — served by the laundry branch circuit already counted at 1,500 VA (item (2))',
        demandVA: 0,
      });
      continue;
    }

    if (row.role === 'existing') {
      existingPoolVA += row.totalVA;
      lineItems.push({
        id: row.eq.id,
        label: rowLabel(row),
        origin: 'existing',
        connectedVA: row.totalVA,
        treatment:
          row.eq.equipment_type === 'Dryer'
            ? `${existingTreatment} — counted under item (3)(c)`
            : existingTreatment,
        demandVA: null,
      });
      continue;
    }

    let treatment: string;
    if (row.kind === 'evse') {
      newEvseVA += row.totalVA;
      treatment = `${rates.newEvse * 100}% (new EVSE)`;
    } else if (countsAsCentralResistanceHeat(row.eq, input.calcRules ?? 1)) {
      newResistVA += row.totalVA;
      treatment = `${rates.newCentralResistanceHeat * 100}% (new central electric resistance space heating)`;
      if (row.kind === 'heatPump') {
        treatment +=
          row.eq.includes_backup_heat === true
            ? ' — heat pump rating includes backup strip heat'
            : ' — heat pump not stated to exclude backup strip heat';
      }
    } else {
      otherNewVA += row.totalVA;
      treatment = `${rates.otherNewLoads * 100}% (all other new loads)`;
      if (row.eq.equipment_type === 'Dryer') {
        treatment += ' — counted under item (3)(c)';
      }
    }
    lineItems.push({
      id: row.eq.id,
      label: rowLabel(row),
      origin: 'proposed',
      connectedVA: row.totalVA,
      treatment,
      demandVA: null,
    });
  }

  // First tier applies to existing and new load combined. Existing fills it
  // first; any remaining capacity absorbs new loads lowest-percentage-first
  // (position P4 — conservative).
  const cap = pack.optional83TierKVA * 1000;
  const tierFromExistingVA = Math.min(existingPoolVA, cap);
  let tierLeft = cap - tierFromExistingVA;

  const absorb = (va: number): number => {
    const absorbed = Math.min(va, tierLeft);
    tierLeft -= absorbed;
    return absorbed;
  };

  const otherAbsorbed = absorb(otherNewVA);
  const evseAbsorbed = absorb(newEvseVA);
  const resistAbsorbed = absorb(newResistVA);
  const tierFromNewVA = otherAbsorbed + evseAbsorbed + resistAbsorbed;
  if (tierFromNewVA > 0) {
    positionsApplied.push(ENGINE_POSITIONS.P4_2026_TIER_FILL);
  }
  if ([...existing, ...proposed].some((r) => r.kind === 'evse' && !isExcluded(r))) {
    positionsApplied.push(ENGINE_POSITIONS.P5_NO_CONTINUOUS_EVSE_FACTOR);
  }

  const existingRemainderDemandVA = 0.4 * (existingPoolVA - tierFromExistingVA);
  const otherNewDemandVA =
    otherAbsorbed + rates.otherNewLoads * (otherNewVA - otherAbsorbed);
  const newEvseDemandVA = evseAbsorbed + rates.newEvse * (newEvseVA - evseAbsorbed);
  const newResistDemandVA =
    resistAbsorbed + rates.newCentralResistanceHeat * (newResistVA - resistAbsorbed);

  const totalDemandVA =
    tierFromExistingVA +
    existingRemainderDemandVA +
    otherNewDemandVA +
    newEvseDemandVA +
    newResistDemandVA;

  return finalize({
    submethod: '120.83',
    pack,
    serviceCapacityKVA,
    lineItems,
    totalConnectedVA: existingPoolVA + newEvseVA + newResistVA + otherNewVA,
    excludedNoncoincidentVA,
    poolVA: existingPoolVA,
    tierVA: tierFromExistingVA + tierFromNewVA,
    tierDemandVA: tierFromExistingVA + tierFromNewVA,
    poolDemandVA: tierFromExistingVA + existingRemainderDemandVA,
    hvacBucketVA: 0,
    evseFullVA: 0,
    evseFullDemandVA: 0,
    categories2026: {
      existingPoolVA,
      tierFromExistingVA,
      tierFromNewVA,
      existingRemainderDemandVA,
      newEvseVA,
      newEvseDemandVA,
      newCentralResistanceHeatVA: newResistVA,
      newCentralResistanceHeatDemandVA: newResistDemandVA,
      otherNewVA,
      otherNewDemandVA,
    },
    totalDemandVA,
    positionsApplied,
    warnings,
  });
}

interface FinalizeArgs {
  submethod: TraditionalSubmethod;
  pack: EditionPack;
  serviceCapacityKVA: number;
  lineItems: TraditionalLineItem[];
  totalConnectedVA: number;
  excludedNoncoincidentVA: number;
  poolVA: number;
  tierVA: number;
  tierDemandVA: number;
  poolDemandVA: number;
  hvacBucketVA: number;
  evseFullVA: number;
  evseFullDemandVA: number;
  statedParameters?: TraditionalCalcResults['statedParameters'];
  categories2026?: TraditionalCalcResults['categories2026'];
  totalDemandVA: number;
  positionsApplied: string[];
  warnings: string[];
}

function finalize(args: FinalizeArgs): TraditionalCalcResults {
  const totalDemandKVA = args.totalDemandVA / 1000;
  const adjustedDemandKVA = totalDemandKVA;
  const availableMarginKVA = args.serviceCapacityKVA - adjustedDemandKVA;
  const utilizationPercent =
    args.serviceCapacityKVA > 0
      ? (adjustedDemandKVA / args.serviceCapacityKVA) * 100
      : 0;
  const adequacyResult: 'PASS' | 'FAIL' =
    args.serviceCapacityKVA > 0 && adjustedDemandKVA <= args.serviceCapacityKVA
      ? 'PASS'
      : 'FAIL';

  const base = args.pack.sectionMap.existingDwelling;
  const sectionCitation =
    args.submethod === '220.83A'
      ? `${base}(A)`
      : args.submethod === '220.83B'
        ? `${base}(B)`
        : `${base}`;

  return {
    method: 'traditional',
    submethodApplied: args.submethod,
    codeEdition: args.pack.edition,
    sectionCitation,
    lineItems: args.lineItems,
    totalConnectedVA: args.totalConnectedVA,
    excludedNoncoincidentVA: args.excludedNoncoincidentVA,
    poolVA: args.poolVA,
    tierVA: args.tierVA,
    tierDemandVA: args.tierDemandVA,
    poolDemandVA: args.poolDemandVA,
    hvacBucketVA: args.hvacBucketVA,
    evseFullVA: args.evseFullVA,
    evseFullDemandVA: args.evseFullDemandVA,
    statedParameters: args.statedParameters,
    categories2026: args.categories2026,
    totalDemandVA: args.totalDemandVA,
    totalDemandKVA,
    serviceCapacityKVA: args.serviceCapacityKVA,
    adjustedDemandKVA,
    availableMarginKVA,
    utilizationPercent,
    adequacyResult,
    positionsApplied: args.positionsApplied,
    warnings: args.warnings,
  };
}
